import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { freshnessLabel, freshnessBadgeClasses } from "../lib/metricEnvelope";

const API = process.env.REACT_APP_BACKEND_URL;

function readCustomerId() {
  try {
    const raw = sessionStorage.getItem("current_customer");
    if (!raw) return "";
    return JSON.parse(raw)?.id || "";
  } catch { return ""; }
}

const ROLES = [
  { id: "ceo", label: "CEO",
    accent: "from-blue-50 to-blue-100 border-blue-200" },
  { id: "cfo", label: "CFO",
    accent: "from-emerald-50 to-emerald-100 border-emerald-200" },
  { id: "cmo", label: "CMO",
    accent: "from-violet-50 to-violet-100 border-violet-200" },
  { id: "cto", label: "CTO",
    accent: "from-amber-50 to-amber-100 border-amber-200" },
];

// EXECUTIVE BRIEFING ASYNC REFACTOR (2026-08) — real backend stages
// (executive_intelligence_router.py's _run_brief_job) mapped to
// user-facing copy. Mirrors the Growth Mentor async job pattern
// (GrowthMentor.jsx) that fixed the same class of Kimi-timeout bug.
const STAGE_LABELS = {
  queued:            "Queuing briefing…",
  loading_context:   "Loading validated financials + strategic context…",
  generating_brief:  "Generating briefing (this can take up to a minute)…",
  finalising_brief:  "Finalising briefing…",
};
const POLL_INTERVAL_MS = 2000;
// executive_intelligence.py's generate_brief() uses llm_send(..., timeout=
// 120.0) (raised 2026-08 — see that file's comment for the latency
// measurement that drove it). Give the poll loop headroom above that
// worst case so we never show a false timeout while the backend is
// still legitimately working.
const POLL_TIMEOUT_MS = 150_000;

const SEVERITY_CLASS = {
  high:   "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low:    "bg-emerald-50 text-emerald-700 border-emerald-200",
};

/**
 * Executive Briefing page (P2.2 surface).
 *
 * Generates / displays role-specific C-ORE briefings driven by the
 * Executive Intelligence Engine. Briefs are read from
 *   GET /api/executive-intelligence/brief?role=cfo|ceo|cto|cmo
 * which itself reads ONLY from validated history + Calculation Engine
 * outputs — never raw uploads.
 *
 * Users can accept / dismiss each recommendation; both write to
 * Business Memory so future briefs avoid repeating the same advice.
 */
export default function ExecutiveBriefingPage() {
  const customerId = readCustomerId();
  const [role, setRole] = useState("ceo");
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stage, setStage] = useState(null);
  const [error, setError] = useState(null);
  const [actionPending, setActionPending] = useState({}); // recommendation_id → status
  const [actionError, setActionError]     = useState({}); // recommendation_id → error message
  const pollRef  = useRef(null);
  const jobIdRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // EXECUTIVE BRIEFING ASYNC REFACTOR (2026-08) — polls
  // GET /brief/status/{job_id} until completed/failed. Same pattern as
  // GrowthMentor.jsx's pollJob() (Fix Pack 1A), which fixed the same
  // class of Kimi-timeout bug for Growth Mentor generation.
  const pollJob = useCallback((jobId, startedAt) => {
    if (jobIdRef.current !== jobId) return; // superseded by a newer generate()
    (async () => {
      try {
        const res = await fetch(`${API}/api/executive-intelligence/brief/status/${jobId}`);
        if (!res.ok) throw new Error("status_check_failed");
        const j = await res.json();
        if (jobIdRef.current !== jobId) return; // stale response, a retry started meanwhile

        if (j.status === "completed") {
          setBrief(j.brief);
          setStage(null);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        if (j.status === "failed") {
          setError(j.failure_reason || "Executive briefing could not be generated. Please retry.");
          setStage(null);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setError("Briefing generation is taking longer than expected. Please retry.");
          setStage(null);
          setLoading(false);
          setRefreshing(false);
          return;
        }
        setStage(j.stage || "queued");
        pollRef.current = setTimeout(() => pollJob(jobId, startedAt), POLL_INTERVAL_MS);
      } catch (_e) {
        if (jobIdRef.current !== jobId) return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setError("Executive briefing could not be generated. Please retry.");
          setStage(null);
          setLoading(false);
          setRefreshing(false);
        } else {
          pollRef.current = setTimeout(() => pollJob(jobId, startedAt), POLL_INTERVAL_MS);
        }
      }
    })();
  }, []);

  // Kicks off POST /brief/generate then starts polling for the result.
  const generateBrief = useCallback(async (forceRefresh) => {
    if (forceRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    setStage("queued");
    if (pollRef.current) clearTimeout(pollRef.current);
    try {
      const res = await fetch(`${API}/api/executive-intelligence/brief/generate`, {
        method:  "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ role }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json())?.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      const { job_id } = await res.json();
      jobIdRef.current = job_id;
      pollJob(job_id, Date.now());
    } catch (e) {
      setError(e.message || String(e));
      setLoading(false);
      setRefreshing(false);
      setStage(null);
    }
  }, [customerId, role, pollJob]);

  // Cache-only check first (GET /brief never calls the LLM anymore) —
  // only falls through to the async generation job when there's no
  // fresh stored brief for this (customer, role).
  const fetchBrief = useCallback(async (forceRefresh) => {
    if (!customerId) { setError("Please sign in to view briefings."); setLoading(false); return; }
    if (forceRefresh) {
      await generateBrief(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/executive-intelligence/brief?role=${role}`,
        { credentials: "include" },
      );
      const body = await r.json();
      if (!r.ok) {
        setError(body?.detail || `HTTP ${r.status}`);
        setLoading(false);
        return;
      }
      if (body?.cached && body?.brief) {
        setBrief(body.brief);
        setLoading(false);
      } else {
        await generateBrief(false);
      }
    } catch (e) {
      setError(String(e));
      setLoading(false);
    }
  }, [customerId, role, generateBrief]);

  useEffect(() => {
    fetchBrief(false);
  }, [fetchBrief]);

  const handleAction = async (rec, action, reason) => {
    if (!customerId) return;
    setActionPending((p) => ({ ...p, [rec.id]: action }));
    setActionError((e) => ({ ...e, [rec.id]: null }));
    try {
      const r = await fetch(`${API}/api/executive-intelligence/feedback`, {
        method:  "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          role,
          recommendation_id: rec.id,
          title:             rec.title,
          // Carried so an ACCEPTED recommendation can become a real
          // Command Center Action-layer item (Three-Layer Command
          // Center, 2026-08) without re-fetching this historical brief.
          detail:            rec.detail,
          priority:          rec.priority,
          expected_impact:   rec.expected_impact,
          horizon:           rec.horizon,
          action,
          reason,
        }),
      });
      if (!r.ok) {
        let detail = `HTTP ${r.status}`;
        try { const body = await r.json(); detail = body?.detail || detail; } catch { /* ignore */ }
        setActionPending((p) => ({ ...p, [rec.id]: null }));
        // Friendly mapping for the known dev-state — migration missing
        const friendly = detail === "memory_write_failed"
          ? "Couldn't save your choice (Business Memory table is being set up). Please retry shortly."
          : `Couldn't save (${detail}).`;
        setActionError((e) => ({ ...e, [rec.id]: friendly }));
        return;
      }
      setActionPending((p) => ({ ...p, [rec.id]: action + "ed" }));
    } catch (err) {
      setActionPending((p) => ({ ...p, [rec.id]: null }));
      setActionError((e) => ({ ...e, [rec.id]:
        `Network error — please retry. (${String(err)})` }));
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-10 flex items-center gap-2.5 text-[13px] text-slate-500"
           data-testid="briefing-loading">
        <Loader2 size={15} className="animate-spin shrink-0" />
        <span>{stage ? (STAGE_LABELS[stage] || STAGE_LABELS.queued) : "Loading briefing…"}</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-10 flex items-center justify-between gap-3 text-[13px] text-rose-600"
           data-testid="briefing-error">
        <span>{error}</span>
        <button
          type="button"
          data-testid="briefing-retry-btn"
          onClick={() => fetchBrief(true)}
          className="shrink-0 rounded-md border border-rose-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-rose-700 hover:bg-rose-100"
        >
          Retry
        </button>
      </div>
    );
  }

  const emptyReason = brief?.data_quality?.empty_reason;
  const noData      = (brief?.observations || []).length === 0 &&
                      (brief?.recommended_actions || []).length === 0 &&
                      !brief?.executive_summary;

  return (
    <div className="px-4 tablet:px-5 desktop:px-6 py-5 desktop:py-8 max-w-5xl mx-auto" data-testid="briefing-page">
      <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
      <h1 className="text-2xl desktop:text-3xl font-semibold text-slate-900">
          Executive Briefing
        </h1>
        {brief?.as_of_period?.freshness_label && (
          <span data-testid="briefing-as-of"
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11.5px] ${freshnessBadgeClasses(brief.as_of_period)}`}>
            {brief.as_of_period.freshness_label}
          </span>
        )}
      </div>
      <p className="text-[14px] text-slate-600 mb-5 max-w-2xl">
        AI-generated from your validated financial history + Calculation
        Engine outputs. Never from raw uploads. Switch the role tab to see
        the same data through a different executive&apos;s lens.
      </p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="briefing-role-tabs">
        {ROLES.map((r) => (
          <button
            key={r.id}
            type="button"
            data-testid={`briefing-role-${r.id}`}
            onClick={() => setRole(r.id)}
            className={`rounded-full border px-4 py-1.5 text-[12.5px] font-medium transition-all
              ${role === r.id
                ? "bg-[#0F1B2D] text-white border-[#0F1B2D]"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
          >
            {r.label}
          </button>
        ))}
        <button
          type="button"
          data-testid="briefing-refresh"
          disabled={refreshing}
          onClick={() => fetchBrief(true)}
          className="ml-auto rounded-full border border-slate-200 bg-white px-4 py-1.5 text-[12.5px] font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-50"
        >
          {refreshing ? "Regenerating…" : "Regenerate"}
        </button>
      </div>

      {refreshing && stage && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-[12px] text-blue-800"
             data-testid="briefing-regenerating-status">
          <Loader2 size={13} className="animate-spin shrink-0" />
          <span>{STAGE_LABELS[stage] || STAGE_LABELS.queued}</span>
        </div>
      )}

      {brief?.data_quality?.warnings?.length > 0 && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
             data-testid="briefing-warnings">
          <div className="font-semibold mb-0.5">Data quality notes</div>
          <ul className="list-disc list-inside space-y-0.5">
            {brief.data_quality.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {noData ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center"
             data-testid="briefing-empty">
          <p className="text-[14px] text-slate-600 mb-4">
            {emptyReason === "no_validated_history"
              ? "No validated financial history yet. Upload at least one statement to see your first briefing."
              : "The C-ORE briefing engine is temporarily unavailable. Your metric data is unchanged — please try again in a minute."}
          </p>
          {emptyReason === "no_validated_history" && (
            <Link to="/financial-sync"
                  className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-700">
              Upload a statement →
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {brief?.executive_summary && (
            <Section title="Executive Summary" testId="briefing-summary">
              <p className="text-[14px] leading-relaxed text-slate-800">
                {brief.executive_summary}
              </p>
            </Section>
          )}

          {brief?.role_briefing && (
            <Section title={`${brief.role_title || "Executive"} POV`}
                     testId="briefing-role-briefing">
              <p className="text-[13.5px] leading-relaxed text-slate-700 italic">
                {brief.role_briefing}
              </p>
            </Section>
          )}

          {(brief?.observations || []).length > 0 && (
            <Section title="Observations" testId="briefing-observations">
              <ul className="space-y-3">
                {brief.observations.map((o, i) => (
                  <li key={i} data-testid={`briefing-obs-${i}`}
                      className="border-l-2 border-slate-200 pl-3">
                    <div className="text-[13.5px] font-semibold text-slate-900">{o.title}</div>
                    <div className="text-[13px] text-slate-700 mt-0.5">{o.detail}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(brief?.risks || []).length > 0 && (
            <Section title="Risks" testId="briefing-risks">
              <ul className="space-y-3">
                {brief.risks.map((r, i) => (
                  <li key={i} data-testid={`briefing-risk-${i}`}
                      className="flex gap-3">
                    <span className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase ${SEVERITY_CLASS[r.severity] || SEVERITY_CLASS.medium}`}>
                      {r.severity || "medium"}
                    </span>
                    <div>
                      <div className="text-[13.5px] font-semibold text-slate-900">{r.title}</div>
                      <div className="text-[13px] text-slate-700 mt-0.5">{r.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(brief?.opportunities || []).length > 0 && (
            <Section title="Opportunities" testId="briefing-opportunities">
              <ul className="space-y-3">
                {brief.opportunities.map((o, i) => (
                  <li key={i} data-testid={`briefing-opp-${i}`}
                      className="border-l-2 border-emerald-200 pl-3">
                    <div className="text-[13.5px] font-semibold text-slate-900">{o.title}</div>
                    <div className="text-[13px] text-slate-700 mt-0.5">{o.detail}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(brief?.root_cause_analysis || []).length > 0 && (
            <Section title="Root-Cause Analysis" testId="briefing-rca">
              <ul className="space-y-3">
                {brief.root_cause_analysis.map((r, i) => (
                  <li key={i} data-testid={`briefing-rca-${i}`}>
                    <div className="text-[13.5px] font-semibold text-slate-900">
                      {r.observation_title}
                    </div>
                    {Array.isArray(r.likely_causes) && r.likely_causes.length > 0 && (
                      <ul className="mt-1 list-disc list-inside text-[13px] text-slate-700">
                        {r.likely_causes.map((c, j) => <li key={j}>{c}</li>)}
                      </ul>
                    )}
                    {r.verify_action && (
                      <div className="mt-1 text-[12px] text-slate-500">
                        Verify by: {r.verify_action}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(brief?.recommended_actions || []).length > 0 && (
            <Section title="Recommended Actions" testId="briefing-actions">
              <ul className="space-y-3">
                {brief.recommended_actions
                  .slice()
                  .sort((a, b) => (a.priority || 5) - (b.priority || 5))
                  .map((rec, i) => {
                  const state = actionPending[rec.id];
                  return (
                    <li key={rec.id || i}
                        data-testid={`briefing-rec-${i}`}
                        className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10.5px] font-bold text-white">
                              P{rec.priority || 3}
                            </span>
                            <div className="text-[13.5px] font-semibold text-slate-900">{rec.title}</div>
                          </div>
                          <div className="mt-1 text-[13px] text-slate-700">{rec.detail}</div>
                          {rec.expected_impact && (
                            <div className="mt-1 text-[12px] text-emerald-700">
                              Expected impact: {rec.expected_impact}
                            </div>
                          )}
                          {rec.horizon && (
                            <div className="mt-0.5 text-[11px] text-slate-500">
                              Horizon: {rec.horizon}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            type="button"
                            data-testid={`briefing-rec-${i}-accept`}
                            disabled={!!state}
                            onClick={() => handleAction(rec, "accept")}
                            className={`rounded-md px-3 py-1 text-[11.5px] font-medium border transition-colors ${
                              state === "accepted"
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            }`}
                          >
                            {state === "accepted" ? "Accepted" : "Accept"}
                          </button>
                          <button
                            type="button"
                            data-testid={`briefing-rec-${i}-dismiss`}
                            disabled={!!state}
                            onClick={() => handleAction(rec, "dismiss")}
                            className={`rounded-md px-3 py-1 text-[11.5px] font-medium border transition-colors ${
                              state === "dismissed"
                                ? "bg-slate-700 text-white border-slate-700"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                            }`}
                          >
                            {state === "dismissed" ? "Dismissed" : "Dismiss"}
                          </button>
                        </div>
                      </div>
                      {actionError[rec.id] && (
                        <div data-testid={`briefing-rec-${i}-error`}
                             className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11.5px] text-amber-800">
                          {actionError[rec.id]}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {(brief?.forward_insights || []).length > 0 && (
            <Section title="Forward-looking Insights" testId="briefing-forward">
              <ul className="space-y-3">
                {brief.forward_insights.map((f, i) => (
                  <li key={i} data-testid={`briefing-forward-${i}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10.5px] font-semibold text-slate-700">
                        {f.horizon || "?"}
                      </span>
                      <div className="text-[13.5px] font-semibold text-slate-900">{f.title}</div>
                    </div>
                    <div className="mt-1 text-[13px] text-slate-700">{f.detail}</div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {(brief?.previous_recommendations_context?.dismissed?.length > 0
            || brief?.previous_recommendations_context?.accepted?.length > 0) && (
            <Section title="Memory: prior decisions" testId="briefing-memory">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12.5px]">
                <div>
                  <div className="font-semibold text-slate-600 mb-1">Previously accepted</div>
                  <ul className="space-y-1 text-slate-700">
                    {(brief.previous_recommendations_context?.accepted || []).map((p, i) => (
                      <li key={i}>· {p.title}</li>
                    ))}
                    {(brief.previous_recommendations_context?.accepted || []).length === 0 && (
                      <li className="text-slate-400">None yet</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="font-semibold text-slate-600 mb-1">Previously dismissed</div>
                  <ul className="space-y-1 text-slate-700">
                    {(brief.previous_recommendations_context?.dismissed || []).map((p, i) => (
                      <li key={i}>· {p.title}</li>
                    ))}
                    {(brief.previous_recommendations_context?.dismissed || []).length === 0 && (
                      <li className="text-slate-400">None yet</li>
                    )}
                  </ul>
                </div>
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId}
             className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="text-[15px] font-semibold text-slate-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}
